import {
	Box,
	FormControl,
	FormHelperText,
	FormLabel,
	Text,
	useColorModeValue,
} from '@chakra-ui/react';
import { AsyncCreatableSelect } from 'chakra-react-select';
import { useId, useMemo, useRef } from 'react';
import { typography } from 'theme/tokens';
import { translate } from '../../../i18n';
import { CompanySearchResult, searchCompanies } from '../utils/recherche-entreprises';

const DEBOUNCE_MS = 300;
// The recherche-entreprises API rejects queries shorter than 3 characters (HTTP 400).
const MIN_QUERY_LENGTH = 3;

interface CompanyOption {
	label: string;
	value: string;
	company?: CompanySearchResult;
}

interface CompanyNameFieldProps {
	label: string;
	value: string;
	onChange: (name: string) => void;
	placeholder?: string;
	helperText?: string;
	isRequired?: boolean;
	isDisabled?: boolean;
}

/**
 * Autocomplete for an organization's legal name ("Dénomination Sociale").
 * Suggests French companies from the public recherche-entreprises registry;
 * selecting one fills the name. Free-typed names are still accepted (creatable
 * + committed on blur), so non-registered or foreign companies are not blocked.
 */
export default function CompanyNameField({
	label,
	value,
	onChange,
	placeholder,
	helperText,
	isRequired,
	isDisabled,
}: CompanyNameFieldProps) {
	const labelColor = useColorModeValue('secondaryGray.900', 'white');
	const helperColor = useColorModeValue('secondaryGray.500', 'gray.400');
	const metaColor = useColorModeValue('secondaryGray.600', 'gray.400');
	const inputId = useId();

	const timerRef = useRef<ReturnType<typeof setTimeout>>();
	const abortRef = useRef<AbortController>();
	// Last free-typed text, so we can commit it on blur even if no option is picked.
	const pendingInputRef = useRef('');

	// Debounced, abortable loader. react-select keeps only the latest resolution,
	// so resolving stale/aborted requests with [] is safe.
	const loadOptions = useMemo(
		() => (input: string) =>
			new Promise<CompanyOption[]>((resolve) => {
				if (timerRef.current) clearTimeout(timerRef.current);
				abortRef.current?.abort();

				if (input.trim().length < MIN_QUERY_LENGTH) {
					resolve([]);
					return;
				}

				timerRef.current = setTimeout(async () => {
					const controller = new AbortController();
					abortRef.current = controller;
					try {
						const companies = await searchCompanies(input, controller.signal);
						resolve(
							companies.map((c) => ({ label: c.name, value: c.siren, company: c })),
						);
					} catch (error) {
						if (!controller.signal.aborted) {
							console.warn('Company search failed', error);
						}
						resolve([]);
					}
				}, DEBOUNCE_MS);
			}),
		[],
	);

	const currentValue: CompanyOption | null = value ? { label: value, value } : null;

	// Render the dropdown in a body-level portal so it isn't clipped by the
	// PageCard's overflow (the card sets overflowY:hidden + overflowX:scroll for
	// rounded corners / small-screen scroll, which would otherwise cut it off).
	const menuPortalTarget = typeof document !== 'undefined' ? document.body : undefined;

	const commit = (name: string) => {
		pendingInputRef.current = '';
		onChange(name);
	};

	return (
		<FormControl isRequired={isRequired} isDisabled={isDisabled}>
			<FormLabel htmlFor={inputId} {...typography.formLabel} color={labelColor} mb="6px">
				{label}
			</FormLabel>

			<AsyncCreatableSelect
				inputId={inputId}
				isClearable
				isDisabled={isDisabled}
				value={currentValue}
				loadOptions={loadOptions}
				defaultOptions={false}
				menuPortalTarget={menuPortalTarget}
				menuPosition="fixed"
				styles={{ menuPortal: (base) => ({ ...base, zIndex: 1500 }) }}
				placeholder={placeholder}
				loadingMessage={() => translate('organization.company_search_loading')}
				noOptionsMessage={({ inputValue }) =>
					inputValue.trim().length < MIN_QUERY_LENGTH
						? null
						: translate('organization.company_search_no_results')
				}
				formatCreateLabel={(input) =>
					`${translate('organization.company_use')} "${input}"`
				}
				onInputChange={(input, meta) => {
					if (meta.action === 'input-change') pendingInputRef.current = input;
				}}
				onChange={(option) => {
					const o = option as CompanyOption | null;
					commit(o ? o.company?.name ?? o.label : '');
				}}
				onBlur={() => {
					const typed = pendingInputRef.current.trim();
					if (typed) commit(typed);
				}}
				formatOptionLabel={(option, meta) => {
					const o = option as CompanyOption;
					// In the control (selected value) or for a free-typed entry, show plain name.
					if (meta.context === 'value' || !o.company) return o.label;
					const location = [o.company.postalCode, o.company.city]
						.filter(Boolean)
						.join(' ');
					const details = [location, o.company.sector].filter(Boolean).join(' · ');
					return (
						<Box>
							<Text fontWeight="600">{o.company.name}</Text>
							{details && (
								<Text fontSize="xs" color={metaColor}>
									{details}
								</Text>
							)}
						</Box>
					);
				}}
				chakraStyles={{
					container: (base: any) => ({ ...base, w: '100%' }),
					control: (base: any) => ({
						...base,
						borderRadius: '12px',
						borderColor: 'secondaryGray.100',
						bg: 'gray.50',
						_focus: {
							borderColor: 'brand.500',
							boxShadow: '0 0 0 2px rgba(2, 63, 127, 0.15)',
						},
					}),
				}}
			/>

			{helperText && (
				<FormHelperText fontSize="xs" color={helperColor} mt="4px">
					{helperText}
				</FormHelperText>
			)}
		</FormControl>
	);
}
